import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@/core/LoadingSpinner';
import { useTheme } from '@/theme/useTheme';

import { EChartActions } from './EChartActions';

interface ChartProps {
  width?: string;
  height?: string;
  options: any;
  className?: string;
  exportPdf?: boolean;
  exportCsv?: boolean;
  exportExcel?: boolean;
  exportPng?: boolean;
  exportTitle?: string;
  /** Called with ECharts' event params when a chart element is clicked. */
  onClick?: (params: any) => void;
}

export const EChart = React.forwardRef<any, ChartProps>(
  (
    { width = '100%', height = '100%', options, className, onClick, ...props },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<any>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    // `loading` is too late for this: both effects that draw run on mount,
    // before the state set by the first can stop the second.
    const drawingRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const { theme } = useTheme();
    const previousThemeRef = useRef(theme);
    // The latest handler, so the listener bound at init never goes stale.
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;

    // The live ECharts instance, also in state: a theme switch disposes it and
    // creates another inside an async import, after the last render, so the
    // forwarded ref and EChartActions must re-render to see the new one.
    // Otherwise they keep the disposed instance, whose getDataURL() returns
    // undefined (and a PNG export then saved the page as HTML).
    const [instance, setInstance] = useState<any>(null);
    const attach = (chart) => {
      chartRef.current = chart;
      setInstance(chart);
      chart.off('click');
      chart.on('click', (params) => onClickRef.current?.(params));
    };

    React.useImperativeHandle(ref, () => instance, [instance]);

    useEffect(() => {
      drawChart();

      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        if (chartRef.current) {
          chartRef.current.dispose();
          chartRef.current = null;
          containerRef.current = null;
        }
      };
    }, []);

    // Update chart with new theme. It must not run on mount: the mount effect
    // above is already drawing, and a second pass here either re-enters
    // drawChart (the dynamic import has not resolved yet, so chartRef is still
    // empty) or disposes the fresh instance and re-initialises it. Either way
    // the entry animation plays twice on every page load.
    useEffect(() => {
      if (previousThemeRef.current === theme) return;
      previousThemeRef.current = theme;
      if (!containerRef.current) return;

      import('@/echarts').then((module) => {
        if (!containerRef.current) return;
        const echarts = module.default;

        if (chartRef.current) {
          chartRef.current.dispose();
          attach(echarts.init(containerRef.current, `${theme}-metronic`));
          renderChart();
        } else {
          drawChart();
        }
      });
    }, [theme]);

    useEffect(() => {
      if (chartRef.current) {
        renderChart();
      } else if (!chartRef.current && !loading) {
        drawChart();
      }
    }, [options, loading]);

    const drawChart = () => {
      if (drawingRef.current) return;
      drawingRef.current = true;
      setLoading(true);
      import('@/echarts')
        .then((module) => {
          setLoading(false);
          if (!containerRef.current) {
            return;
          }
          const echarts = module.default;
          const chart = echarts.getInstanceByDom(containerRef.current);
          if (!chart) {
            attach(echarts.init(containerRef.current, `${theme}-metronic`));
          }
          renderChart();
          if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver((entries) => {
              entries.forEach(({ target }) => {
                const instance = echarts.getInstanceByDom(
                  target as HTMLElement,
                );
                if (instance) {
                  instance.resize();
                }
              });
            });
          }
          resizeObserverRef.current.observe(containerRef.current);
        })
        .finally(() => {
          // Also after a failed import, so a theme switch can still draw.
          drawingRef.current = false;
        });
    };

    const renderChart = () => {
      if (chartRef.current) {
        chartRef.current.setOption(options, `${theme}-metronic`);
      }
    };

    useEffect(() => {
      const handleResize = () => {
        if (chartRef.current) {
          chartRef.current.resize();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [instance]);

    const style = { width, height };

    return (
      <div className={className} style={style}>
        {loading && <LoadingSpinner />}
        <EChartActions chartInstance={instance} {...props} />
        <div
          className={classNames({ hidden: loading })}
          style={style}
          ref={containerRef}
        />
      </div>
    );
  },
);
